import React, { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { BottomNavigation, BottomNavigationAction, Paper } from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import GroupsIcon from '@mui/icons-material/Groups';
import EventIcon from '@mui/icons-material/Event';
import InsightsIcon from '@mui/icons-material/Insights';
import SearchIcon from '@mui/icons-material/Search';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { useAuth } from './AuthGate';

export default function MobileNav({ onMoreClick, onSearchClick }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { canRead } = useAuth();

  // Search is inserted before "More" only when the user holds search:read —
  // hidden entirely for ungranted users, same convention as the desktop bar.
  // Dashboard/Exec/Events are likewise hidden for a registrant-only session
  // (plan 2037 WS-H) — each would otherwise land on an "Access Restricted" page.
  const navItems = useMemo(() => {
    const items = [];
    if (canRead('dashboard')) {
      items.push({ label: 'Dashboard', icon: <DashboardIcon />, path: '/' });
    }
    if (canRead('exec')) {
      items.push({ label: 'Exec', icon: <ShowChartIcon />, path: '/exec/filtered-brief' });
    }
    if (canRead('dashboard')) {
      items.push({ label: 'People', icon: <GroupsIcon />, path: '/participants' });
      items.push({ label: 'Events', icon: <EventIcon />, path: '/events' });
      items.push({ label: 'Insights', icon: <InsightsIcon />, path: '/insights' });
    }
    if (canRead('search')) {
      items.push({ label: 'Search', icon: <SearchIcon />, path: '__search__' });
    }
    items.push({ label: 'More', icon: <MoreHorizIcon />, path: '__more__' });
    return items;
  }, [canRead]);

  const currentIdx = navItems.findIndex(
    (item) => item.path !== '__more__' && item.path !== '__search__' && location.pathname.startsWith(item.path) && item.path !== '/'
  );
  const value = currentIdx >= 0 ? currentIdx : (location.pathname === '/' ? 0 : -1);

  return (
    <Paper component="nav" aria-label="mobile navigation" sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1200 }} elevation={3}>
      <BottomNavigation
        value={value}
        onChange={(_, newValue) => {
          const item = navItems[newValue];
          if (item.path === '__more__') {
            onMoreClick?.();
          } else if (item.path === '__search__') {
            onSearchClick?.();
          } else {
            navigate(item.path);
          }
        }}
        showLabels
        sx={{ height: 56 }}
      >
        {navItems.map((item) => (
          <BottomNavigationAction
            key={item.label}
            label={item.label}
            icon={item.icon}
            sx={{ minWidth: 0, px: 0.5 }}
          />
        ))}
      </BottomNavigation>
    </Paper>
  );
}
